import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useField } from 'react-final-form';

import SectionTitle from '../../components/section-title/SectionTitle';
import MediaPreviewModal from '../../components/media-preview-modal/MediaPreviewModal';
import UsedMediaList from './components/used-media-list/UsedMediaList';
import UsedMediaEmptyDisclaimer from './components/used-media-empty-disclaimer/UsedMediaEmptyDisclaimer';
import useUsedMediaItems from './hooks/useUsedMediaItems';
import { STORY_FORM_FIELDS, STORY_MEDIA_RESTRICTIONS } from '../../constants';
import { setSelectedMedia, setSelectedMediaActiveId } from '../../../../redux/story/storyReducer';
import {
  getStoryMediaId,
  isMediaTypeConflict,
  isStoryMediaDeleted,
  normalizeUsedMedia,
} from '../../media/storyMediaItem';
import useStoryMediaTypeConflict from '../../hooks/useStoryMediaTypeConflict';
import MediaTypeConflictModal from '../../components/media-type-conflict-modal/MediaTypeConflictModal';

import scss from './UsedMedia.module.scss';

function UsedMedia() {
  const { t } = useTranslation('story');
  const dispatch = useDispatch();
  const [previewMedia, setPreviewMedia] = useState(null);

  const { input: mediaInput } = useField(STORY_FORM_FIELDS.MEDIA);

  const { items } = useUsedMediaItems();
  const shouldShowEmptyDisclaimer = false;

  const selectedItems = useMemo(() => mediaInput.value ?? [], [mediaInput.value]);
  const selectedIds = useMemo(() => new Set(selectedItems.map(getStoryMediaId)), [selectedItems]);
  const { isOpen, incomingType, requestAction, confirm, cancel } = useStoryMediaTypeConflict({ selectedItems });

  if (items.length === 0 && !shouldShowEmptyDisclaimer) {
    return null;
  }

  const handleSelect = (media) => {
    if (isStoryMediaDeleted(media)) {
      return;
    }

    const normalized = normalizeUsedMedia(media);

    if (selectedIds.has(normalized.sourceId)) {
      return;
    }

    requestAction(normalized.type, () => {
      const replace = isMediaTypeConflict(selectedItems, normalized.type);
      const nextItems =
        replace || STORY_MEDIA_RESTRICTIONS.SINGLE_SELECTION_ONLY ? [normalized] : [...selectedItems, normalized];

      mediaInput.onChange(nextItems);
      dispatch(setSelectedMedia(nextItems));
      dispatch(setSelectedMediaActiveId(normalized.sourceId));
    });
  };

  const handlePreview = (media) => {
    setPreviewMedia(media);
  };

  const handleClosePreview = () => {
    setPreviewMedia(null);
  };

  return (
    <div className={scss.wrapper}>
      <SectionTitle title={t('usedMedia')} />

      <div className={scss.content}>
        {items.length === 0 && shouldShowEmptyDisclaimer ? (
          <UsedMediaEmptyDisclaimer />
        ) : (
          <UsedMediaList items={items} selectedIds={selectedIds} onSelect={handleSelect} onPreview={handlePreview} />
        )}
      </div>

      <MediaPreviewModal isOpen={previewMedia} onClose={handleClosePreview} media={previewMedia} />

      <MediaTypeConflictModal
        isOpen={isOpen}
        incomingType={incomingType}
        onClose={cancel}
        onCancel={cancel}
        onConfirm={confirm}
      />
    </div>
  );
}

export default UsedMedia;
